import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Typography, Box, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Stage, Layer, Line, Arrow, Text, Rect } from 'react-konva';
import { projectFastenerDrawing } from '../utils/igesExporter';

function bounds(lines) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  (lines || []).forEach((pts) => {
    for (let i = 0; i < pts.length; i += 2) {
      const x = pts[i];
      const y = pts[i + 1];
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  });
  if (!Number.isFinite(minX)) return null;
  return { minX, minY, maxX, maxY, w: maxX - minX, h: maxY - minY };
}

function fmt(n) {
  const r = Math.round(Number(n) * 10) / 10;
  if (!Number.isFinite(r)) return '';
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

function bandX(lines, y0, y1, fallback) {
  let sum = 0;
  let count = 0;
  (lines || []).forEach((pts) => {
    for (let i = 0; i < pts.length; i += 2) {
      const y = pts[i + 1];
      if (y < y0 || y > y1) continue;
      sum += pts[i];
      count += 1;
    }
  });
  return count ? sum / count : fallback;
}

function layoutFloorSheet(payload) {
  const box = bounds(payload.side);
  const m = payload.metrics;
  if (!box || !m) return null;
  const height = Number(m.height) || box.h;
  const leftMm = 96;
  const rightMm = 88;
  const topMm = 42;
  const scale = Math.max(0.4, Math.min(860 / (leftMm + box.w + rightMm), 720 / (topMm + box.h + 20), 1.8));
  const gsScale = Math.max(0.4, Math.min(860 / (leftMm + box.w + rightMm), 720 / (topMm + 555 + 20), 1.8));
  const originX = leftMm * scale;
  const originY = topMm * scale;
  const map = (x, z) => ({
    x: originX + (x - box.minX) * scale,
    y: originY + ((-z + box.maxY) - box.minY) * scale,
  });
  const strokes = (payload.side || []).map((points) => ({
    points: points.flatMap((value, index) => (
      index % 2 === 0
        ? [originX + (value - box.minX) * scale]
        : [originY + (value - box.minY) * scale]
    )),
    sw: 1.15,
  }));
  const dims = [];
  const notes = [];
  const ext = (from, to) => {
    strokes.push({ points: [from.x, from.y, to.x, to.y], sw: 1, dash: [4, 4] });
  };
  const verticalAt = (x1, z1, x2, z2, text, xLineModel) => {
    if (!(Math.abs(z2 - z1) > 1)) return;
    const a = map(x1, z1);
    const b = map(x2, z2);
    const xLine = map(xLineModel, 0).x;
    const side = xLine >= (a.x + b.x) / 2 ? 8 : -8;
    ext(a, { x: xLine + side, y: a.y });
    ext(b, { x: xLine + side, y: b.y });
    dims.push({
      x1: xLine,
      y1: Math.min(a.y, b.y),
      x2: xLine,
      y2: Math.max(a.y, b.y),
      text,
    });
  };
  const pipeBottom = Number(m.pipeBottom);
  const pipeTop = Number(m.pipeTop);
  const pipeRight = Number.isFinite(Number(m.pipeRight)) ? Number(m.pipeRight) : 24.15;
  const upperRadius = Number(m.upperRadius) || 21.2;
  const lowerRadius = 24.15;
  const threadLength = Number(m.threadLength) || 16;
  const upperX = bandX(payload.side, box.minY, box.minY + box.h * 0.22, 0);
  const leg = Number(m.legs) > 0 ? Number(m.legs) : 85;
  const pipeLen = pipeTop > pipeBottom ? pipeTop - pipeBottom : 250;
  const midX = (box.minX + box.maxX) / 2;
  const chainX = pipeRight + 126;
  const singlePipe = !!m.singlePipe;
  verticalAt(midX, 0, pipeRight, leg, fmt(leg), chainX);
  if (!singlePipe) {
    verticalAt(pipeRight, leg, pipeRight, leg + pipeLen, fmt(pipeLen), chainX);
  }
  verticalAt(box.maxX, 0, upperX + upperRadius, height, m.heightLabel || fmt(height), box.maxX + 36);
  if (m.holePitch && dims.length) {
    dims[dims.length - 1].caption = '(Шаг 50мм.)';
  }
  const wallsNear = (z, diameter, center) => {
    const at = (sample) => {
      const paperY = -sample + box.maxY;
      const xs = [];
      (payload.side || []).forEach((pts) => {
        for (let i = 0; i < pts.length - 2; i += 2) {
          const x1 = pts[i];
          const y1 = pts[i + 1];
          const x2 = pts[i + 2];
          const y2 = pts[i + 3];
          if (Math.abs(y2 - y1) < 0.4) continue;
          const yMin = Math.min(y1, y2);
          const yMax = Math.max(y1, y2);
          if (paperY < yMin - 0.6 || paperY > yMax + 0.6) continue;
          const t = Math.min(1, Math.max(0, (paperY - y1) / (y2 - y1)));
          xs.push(x1 + (x2 - x1) * t);
        }
      });
      xs.sort((a, b) => a - b);
      const unique = [];
      xs.forEach((x) => {
        if (!unique.length || x - unique[unique.length - 1] > 0.7) unique.push(x);
      });
      let best = null;
      for (let i = 0; i < unique.length; i += 1) {
        for (let j = i + 1; j < unique.length; j += 1) {
          const err = Math.abs(unique[j] - unique[i] - diameter);
          if (err > 3.5) continue;
          const score = err + Math.abs((unique[i] + unique[j]) / 2 - center) * 0.2;
          if (!best || score < best.score) {
            best = { left: unique[i], right: unique[j], err, score };
          }
        }
      }
      return best;
    };
    let found = null;
    for (let step = 0; step <= 24; step += 3) {
      [0, step, -step].forEach((shift) => {
        const hit = at(z + shift);
        if (hit && (!found || hit.err < found.err)) found = { ...hit, z: z + shift };
      });
      if (found && found.err < 1) break;
    }
    return found;
  };
  const diameterAt = (center, diameter, z, text) => {
    const walls = wallsNear(z, diameter, center);
    const left = walls ? walls.left : center - diameter / 2;
    const right = walls ? walls.right : center + diameter / 2;
    const on = walls ? walls.z : z;
    const start = map(right, on);
    const end = map(left, on);
    dims.push({
      x1: start.x,
      y1: start.y,
      x2: end.x,
      y2: end.y,
      text,
      outsideArrows: true,
    });
  };

  const lowerX = bandX(payload.side, box.minY + box.h * 0.42, box.minY + box.h * 0.68, upperX);
  const threadTeeth = 4;
  const threadPitch = threadLength / threadTeeth;
  const drawSideTeeth = (crestX, rootX) => {
    for (let tooth = 0; tooth < threadTeeth; tooth += 1) {
      const threadZ = height - threadLength + threadPitch * (tooth + 0.5);
      const crest0 = map(crestX, threadZ - threadPitch * 0.32);
      const root = map(rootX, threadZ);
      const crest1 = map(crestX, threadZ + threadPitch * 0.32);
      strokes.push({
        points: [crest0.x, crest0.y, root.x, root.y, crest1.x, crest1.y],
        sw: 1.15,
      });
    }
  };
  if (singlePipe) {
    const seam = wallsNear(height - threadLength * 0.5, upperRadius * 2, upperX);
    const left = seam ? seam.left : upperX - upperRadius;
    const right = seam ? seam.right : upperX + upperRadius;
    const depth = 1.5;
    drawSideTeeth(left, left + depth);
    drawSideTeeth(right, right - depth);
    for (let tooth = 0; tooth < threadTeeth; tooth += 1) {
      const threadZ = height - threadLength + threadPitch * (tooth + 0.5);
      const across = (x1, x2, z) => {
        const a = map(x1, z);
        const b = map(x2, z);
        strokes.push({ points: [a.x, a.y, b.x, b.y], sw: 1.05 });
      };
      across(left, right, threadZ - threadPitch * 0.32);
      across(left, right, threadZ + threadPitch * 0.32);
      across(left + depth, right - depth, threadZ);
    }
  } else {
    [-1, 1].forEach((sign) => {
      drawSideTeeth(upperX + sign * upperRadius, upperX + sign * (upperRadius - 1.6));
    });
  }
  const upperFromTop = threadLength + 28;
  const upperDrop = m.holePitch ? upperFromTop * 2 : upperFromTop * 3;
  const upperZ = singlePipe
    ? Math.min(height - threadLength - 18, Math.max(leg + 36, height - upperDrop))
    : height - upperDrop;
  diameterAt(upperX, upperRadius * 2, upperZ, m.upperPipe || 'Ø42,4×2');
  if (!singlePipe) {
    diameterAt(lowerX, lowerRadius * 2, leg + pipeLen * 0.45, m.lowerPipe || 'Ø48,3×1,5');
  }
  const threadTip = map(upperX, height - threadLength * 0.45);
  const threadKnee = map(upperX - upperRadius - 8, height + 28);
  const threadText = m.thread || '1 1/4" НР';
  const threadWidth = Math.max(78, threadText.length * 8);
  notes.push({
    x: threadKnee.x - threadWidth,
    y: threadKnee.y - 20,
    width: threadWidth,
    text: threadText,
    anchor: 'end',
    shelfX: threadKnee.x - threadWidth,
    shelfY: threadKnee.y,
    kneeX: threadKnee.x,
    tipX: threadTip.x,
    tipY: threadTip.y,
  });
  if (m.holePitch) {
    const centers = (m.holeCenters || [])
      .filter((hole) => Number.isFinite(hole.x) && Number.isFinite(hole.z));
    const pairs = [];
    for (let i = 0; i < centers.length; i += 1) {
      for (let j = i + 1; j < centers.length; j += 1) {
        const gap = Math.abs(centers[i].z - centers[j].z);
        if (Math.abs(gap - Number(m.holePitch)) > 15) continue;
        pairs.push({
          mid: (centers[i].z + centers[j].z) / 2,
          holes: [centers[i], centers[j]],
        });
      }
    }
    pairs.sort((a, b) => b.mid - a.mid);
    const chosen = pairs[1] || pairs[0];
    const pair = chosen ? chosen.holes : null;
    if (pair) {
      const [first, second] = pair;
      verticalAt(
        first.x,
        first.z,
        second.x,
        second.z,
        fmt(m.holePitch),
        Math.max(first.x, second.x) + upperRadius + 46,
      );
    }
  }

  const limits = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
  const grow = (x, y) => {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    if (x < limits.minX) limits.minX = x;
    if (y < limits.minY) limits.minY = y;
    if (x > limits.maxX) limits.maxX = x;
    if (y > limits.maxY) limits.maxY = y;
  };
  strokes.forEach((stroke) => {
    for (let i = 0; i < stroke.points.length; i += 2) grow(stroke.points[i], stroke.points[i + 1]);
  });
  dims.forEach((dim) => {
    grow(dim.x1, dim.y1);
    grow(dim.x2, dim.y2);
    const span = Math.hypot(dim.x2 - dim.x1, dim.y2 - dim.y1) || 1;
    const fs = 16;
    const tw = Math.max(12, String(dim.text).length * fs * 0.62);
    const ux = (dim.x2 - dim.x1) / span;
    const uy = (dim.y2 - dim.y1) / span;
    if (dim.outsideArrows || span < 28) {
      const shelf = tw + 8;
      grow(dim.x1 - ux * 16, dim.y1 - uy * 16);
      grow(dim.x2 + ux * (16 + shelf), dim.y2 + uy * (16 + shelf));
      const cx = dim.x2 + ux * (16 + shelf / 2);
      const cy = dim.y2 + uy * (16 + shelf / 2);
      grow(cx - tw / 2, cy - fs * 1.4);
      grow(cx + tw / 2, cy + fs);
      return;
    }
    const mx = (dim.x1 + dim.x2) / 2;
    const my = (dim.y1 + dim.y2) / 2;
    grow(mx - fs, my - tw / 2);
    grow(mx + fs, my + tw / 2);
    if (dim.caption) {
      const captionW = String(dim.caption).length * fs * 0.55;
      grow(mx + fs * 2, my - captionW / 2);
      grow(mx + fs * 2, my + captionW / 2);
    }
  });
  notes.forEach((note) => {
    grow(note.x, note.y);
    grow((note.x || 0) + (note.width || 78), (note.y || 0) + 18);
    grow(note.shelfX, note.shelfY);
    grow(note.kneeX, note.shelfY);
    grow(note.tipX, note.tipY);
  });
  const pad = 8;
  const partCenter = map(midX, height / 2);
  return {
    minX: limits.minX - pad,
    minY: limits.minY - pad,
    width: Math.ceil(limits.maxX - limits.minX + pad * 2),
    height: Math.ceil(limits.maxY - limits.minY + pad * 2),
    anchorX: partCenter.x,
    maxFit: m.singlePipe ? Math.min(1, gsScale / scale) : undefined,
    strokes,
    dims,
    notes,
  };
}

export function layoutFastenerSheet(payload) {
  if (payload?.metrics?.floor) return layoutFloorSheet(payload);
  const sideB = bounds(payload.side);
  const frontB = bounds(payload.front);
  const m = payload.metrics;
  if (!sideB || !frontB || !m) return null;

  const leftMm = 72;
  const gapMm = 168;
  const rightMm = 56;
  const belowMm = 78;
  const contentW = leftMm + sideB.w + gapMm + frontB.w + rightMm;
  const contentH = Math.max(sideB.h, frontB.h) + belowMm + 12;
  const scale = Math.max(0.35, Math.min(980 / contentW, 760 / contentH, 2.2));

  const topPad = 10;
  const topZ = Math.max(-sideB.minY, -frontB.minY);
  const baseline = topPad + topZ * scale;
  const sideOrigin = {
    x: leftMm * scale,
    y: baseline + sideB.minY * scale,
  };
  const frontOrigin = {
    x: sideOrigin.x + sideB.w * scale + gapMm * scale,
    y: baseline + frontB.minY * scale,
  };

  const mapSide = (y, z) => ({
    x: sideOrigin.x + (y - sideB.minX) * scale,
    y: sideOrigin.y + (-z - sideB.minY) * scale,
  });
  const mapFront = (x, z) => ({
    x: frontOrigin.x + (x - frontB.minX) * scale,
    y: frontOrigin.y + (-z - frontB.minY) * scale,
  });

  const strokes = [];
  const pushLines = (lines, box, origin) => {
    (lines || []).forEach((pts) => {
      const points = [];
      for (let i = 0; i < pts.length; i += 2) {
        points.push(origin.x + (pts[i] - box.minX) * scale);
        points.push(origin.y + (pts[i + 1] - box.minY) * scale);
      }
      if (points.length >= 4) strokes.push({ points, sw: 1.35 });
    });
  };
  pushLines(payload.side, sideB, sideOrigin);
  pushLines(payload.front, frontB, frontOrigin);

  const dims = [];
  const ext = (a, b) => strokes.push({ points: [a.x, a.y, b.x, b.y], sw: 1, dash: [4, 4] });

  const yFace = m.face;
  const yWall = m.overhang;
  const armEnd = -20;
  const partBottom = mapSide(0, 0).y;
  const row130 = partBottom + Math.max(34, 26 * scale);
  const row150 = row130 + Math.max(38, 24 * scale);
  const axisX = mapSide(0, 0).x;
  const armEndX = mapSide(armEnd, 0).x;
  const faceX = mapSide(yFace, 0).x;
  const wallX = mapSide(yWall, 0).x;
  const armUnderside = mapSide(armEnd, m.arms[0] - m.armZ);
  const barBottom = mapSide(yFace, 0).y;
  ext({ x: axisX, y: mapSide(0, m.arms[0] - m.armZ).y + 5 }, { x: axisX, y: row130 + 8 });
  ext({ x: armEndX, y: armUnderside.y + 5 }, { x: armEndX, y: row150 + 8 });
  ext({ x: faceX, y: barBottom + 4 }, { x: faceX, y: row150 + 8 });
  ext({ x: wallX, y: barBottom + 4 }, { x: wallX, y: row150 + 8 });
  dims.push({ x1: axisX, y1: row130, x2: faceX, y2: row130, text: fmt(yFace) });
  dims.push({ x1: armEndX, y1: row150, x2: faceX, y2: row150, text: fmt(yFace - armEnd) });
  const span20 = Math.abs(wallX - faceX);
  const roomy20 = span20 >= 28;
  const y20 = roomy20 ? row150 : row150 + 32;
  if (!roomy20) {
    ext({ x: faceX, y: row150 }, { x: faceX, y: y20 + 8 });
    ext({ x: wallX, y: row150 }, { x: wallX, y: y20 + 8 });
  }
  dims.push({
    x1: faceX,
    y1: y20,
    x2: wallX,
    y2: y20,
    text: '20',
    inside: roomy20,
    outsideArrows: !roomy20,
  });

  if (m.clamps.length > 1) {
    const upper = Math.max(...m.clamps);
    const lower = Math.min(...m.clamps);
    const xAx = mapSide(-m.outerR - 28, 0).x;
    const topClamp = mapSide(-m.outerR, upper);
    const lowClamp = mapSide(-m.outerR, lower);
    ext({ x: topClamp.x - 4, y: topClamp.y }, { x: xAx - 10, y: topClamp.y });
    ext({ x: lowClamp.x - 4, y: lowClamp.y }, { x: xAx - 10, y: lowClamp.y });
    dims.push({
      x1: xAx, y1: topClamp.y, x2: xAx, y2: lowClamp.y, text: fmt(upper - lower), side: 'left',
    });
  }

  const levels = [m.barTop, ...[...m.arms].reverse(), 0];
  const xVert = mapSide(yWall + 42, 0).x;
  levels.forEach((z) => {
    const isArm = m.arms.some((arm) => Math.abs(arm - z) < 0.6);
    const p = mapSide(isArm ? -20 : yWall, z);
    ext(p, { x: xVert + 8, y: p.y });
  });
  for (let i = 0; i < levels.length - 1; i += 1) {
    const a = mapSide(yWall, levels[i]);
    const b = mapSide(yWall, levels[i + 1]);
    dims.push({
      x1: xVert,
      y1: a.y,
      x2: xVert,
      y2: b.y,
      text: fmt(levels[i] - levels[i + 1]),
    });
  }

  if (m.brace) {
    const zArm = m.arms[0] - m.armZ;
    const onArm = mapSide(yFace - 80, zArm);
    const onBar = mapSide(yFace, zArm - 80);
    const drop = 22 + 4 * scale;
    const y80 = onBar.y + drop;
    ext(onArm, { x: onArm.x, y: y80 + 8 });
    ext(onBar, { x: onBar.x, y: y80 + 8 });
    dims.push({ x1: onArm.x, y1: y80, x2: onBar.x, y2: y80, text: '80' });
    const x80 = onArm.x - drop;
    ext(onArm, { x: x80 - 8, y: onArm.y });
    ext(onBar, { x: x80 - 8, y: onBar.y });
    dims.push({
      x1: x80, y1: onBar.y, x2: x80, y2: onArm.y, text: '80', side: 'left',
    });
  }

  if (m.armZ * 2 >= 16) {
    const center = m.arms[0];
    const xDim = mapSide(-52, 0).x;
    const top = mapSide(-20, center + m.armZ);
    const bot = mapSide(-20, center - m.armZ);
    ext(top, { x: xDim - 8, y: top.y });
    ext(bot, { x: xDim - 8, y: bot.y });
    dims.push({
      x1: xDim, y1: top.y, x2: xDim, y2: bot.y, text: fmt(m.armZ * 2), side: 'left',
    });
  }

  const notes = [];
  const topClamp = Math.max(...m.clamps);
  m.clamps.forEach((z) => {
    const center = mapSide(0, z);
    const radius = m.outerR * scale;
    strokes.push({
      points: [center.x - radius - 10, center.y, center.x + radius + 10, center.y],
      sw: 0.6,
      dash: [5, 3],
    });
    strokes.push({
      points: [center.x, center.y - radius - 10, center.x, center.y + radius + 10],
      sw: 0.6,
      dash: [5, 3],
    });
    if (z !== topClamp) return;
    const ux = -0.78;
    const uy = -0.62;
    const near = { x: center.x + ux * radius, y: center.y + uy * radius };
    const far = { x: center.x - ux * radius, y: center.y - uy * radius };
    const reach = radius + 46;
    const knee = { x: center.x + ux * reach, y: center.y + uy * reach };
    const text = `Ø${fmt(m.diameter)}`;
    const shelfW = Math.max(52, text.length * 9);
    notes.push({
      x: knee.x - shelfW,
      y: knee.y - 18,
      width: shelfW,
      text,
      anchor: 'end',
      shelfX: knee.x - shelfW,
      shelfY: knee.y,
      kneeX: knee.x,
      diameter: [near.x, near.y, far.x, far.y],
    });
  });

  const holes = [...m.holes].sort((a, b) => b - a);
  const chain = [m.barTop, ...holes, 0];
  const xPitch = mapFront(-52, 0).x;
  chain.forEach((z) => {
    const isHole = holes.some((hole) => Math.abs(hole - z) < 0.6);
    const p = mapFront(isHole ? -5 : -20, z);
    ext(p, { x: xPitch - 8, y: p.y });
  });
  const labels = [fmt(m.barTop - holes[0])];
  for (let i = 0; i < holes.length - 1; i += 1) labels.push(fmt(holes[i] - holes[i + 1]));
  labels.push(fmt(holes[holes.length - 1]));
  for (let i = 0; i < chain.length - 1; i += 1) {
    const a = mapFront(-20, chain[i]);
    const b = mapFront(-20, chain[i + 1]);
    const shelfDown = i === 0;
    dims.push({
      x1: xPitch,
      y1: shelfDown ? b.y : a.y,
      x2: xPitch,
      y2: shelfDown ? a.y : b.y,
      text: labels[i],
      side: 'left',
    });
  }

  const xHeight = mapFront(56, 0).x;
  const topF = mapFront(20, m.barTop);
  const botF = mapFront(20, 0);
  ext(topF, { x: xHeight + 8, y: topF.y });
  ext(botF, { x: xHeight + 8, y: botF.y });
  dims.push({ x1: xHeight, y1: topF.y, x2: xHeight, y2: botF.y, text: fmt(m.barTop) });

  const yWidth = mapFront(0, 0).y + Math.max(54, 42 * scale);
  const leftF = mapFront(-20, 0);
  const rightF = mapFront(20, 0);
  ext(leftF, { x: leftF.x, y: yWidth + 8 });
  ext(rightF, { x: rightF.x, y: yWidth + 8 });
  dims.push({ x1: leftF.x, y1: yWidth, x2: rightF.x, y2: yWidth, text: '40' });

  const topHole = mapFront(0, holes[0]);
  const holeR = 5 * scale;
  const holeUx = 0.45;
  const holeUy = -0.89;
  const holeNear = { x: topHole.x + holeUx * holeR, y: topHole.y + holeUy * holeR };
  const holeFar = { x: topHole.x - holeUx * holeR, y: topHole.y - holeUy * holeR };
  const holeOut = 14;
  const holeNearOut = { x: topHole.x + holeUx * (holeR + holeOut), y: topHole.y + holeUy * (holeR + holeOut) };
  const holeFarOut = { x: topHole.x - holeUx * (holeR + holeOut), y: topHole.y - holeUy * (holeR + holeOut) };
  const holeKnee = {
    x: holeNearOut.x + holeUx * 42,
    y: holeNearOut.y + holeUy * 42,
  };
  const holeText = 'Ø10';
  const holeShelf = Math.max(40, holeText.length * 9);
  notes.push({
    x: holeKnee.x,
    y: holeKnee.y - 18,
    width: holeShelf,
    text: holeText,
    anchor: 'start',
    shelfX: holeKnee.x + holeShelf,
    shelfY: holeKnee.y,
    kneeX: holeKnee.x,
    diameter: [holeNear.x, holeNear.y, holeFar.x, holeFar.y],
    arrowsOut: [
      [holeNearOut.x, holeNearOut.y, holeNear.x, holeNear.y],
      [holeFarOut.x, holeFarOut.y, holeFar.x, holeFar.y],
    ],
    leaderFrom: [holeNearOut.x, holeNearOut.y],
  });
  const midTop = mapFront(0, m.barTop);
  const midBot = mapFront(0, 0);
  strokes.push({ points: [midTop.x, midTop.y, midBot.x, midBot.y], sw: 0.6, dash: [5, 3] });

  const box = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
  const grow = (x, y) => {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    if (x < box.minX) box.minX = x;
    if (y < box.minY) box.minY = y;
    if (x > box.maxX) box.maxX = x;
    if (y > box.maxY) box.maxY = y;
  };
  strokes.forEach((stroke) => {
    for (let i = 0; i < stroke.points.length; i += 2) grow(stroke.points[i], stroke.points[i + 1]);
  });
  dims.forEach((dim) => {
    grow(dim.x1, dim.y1);
    grow(dim.x2, dim.y2);
    const horizontal = Math.abs(dim.x2 - dim.x1) >= Math.abs(dim.y2 - dim.y1);
    const span = Math.hypot(dim.x2 - dim.x1, dim.y2 - dim.y1) || 1;
    const fs = 16;
    const tw = Math.max(12, String(dim.text).length * fs * 0.62);
    const short = dim.outsideArrows || span < 28;
    if (short) {
      const ux = (dim.x2 - dim.x1) / span;
      const uy = (dim.y2 - dim.y1) / span;
      const shaft = 16;
      const shelf = tw + 8;
      grow(dim.x1 - ux * shaft, dim.y1 - uy * shaft);
      const cx = dim.x2 + ux * (shaft + shelf / 2);
      const cy = dim.y2 + uy * (shaft + shelf / 2);
      grow(dim.x2 + ux * (shaft + shelf), dim.y2 + uy * (shaft + shelf));
      if (horizontal) {
        grow(cx - tw / 2, cy - fs * 1.4);
        grow(cx + tw / 2, cy);
      } else {
        const sign = dim.side === 'right' ? 1 : -1;
        grow(cx + sign * (fs * 0.85 + fs / 2), cy - tw / 2);
        grow(cx, cy + tw / 2);
      }
      return;
    }
    const mx = (dim.x1 + dim.x2) / 2;
    const my = (dim.y1 + dim.y2) / 2;
    const textX = horizontal ? mx : mx + (dim.side === 'right' ? 1 : -1) * (fs * 0.85);
    const textY = horizontal ? my - fs * 0.7 : my;
    if (horizontal) {
      grow(textX - tw / 2, textY - fs / 2);
      grow(textX + tw / 2, textY + fs / 2);
    } else {
      grow(textX - fs / 2, textY - tw / 2);
      grow(textX + fs / 2, textY + tw / 2);
    }
  });
  notes.forEach((note) => {
    grow(note.x, note.y);
    grow((note.x || 0) + (note.width || 78), (note.y || 0) + 18);
    grow(note.shelfX, note.shelfY);
    grow(note.kneeX, note.shelfY);
    (note.diameter || []).forEach((value, index, points) => {
      if (index % 2 === 0) grow(value, points[index + 1]);
    });
    (note.arrowsOut || []).forEach((points) => {
      for (let i = 0; i < points.length; i += 2) grow(points[i], points[i + 1]);
    });
  });
  const pad = 8;
  const minX = box.minX - pad;
  const minY = box.minY - pad;
  const maxX = box.maxX + pad;
  const maxY = box.maxY + pad;

  return {
    minX,
    minY,
    width: Math.ceil(maxX - minX),
    height: Math.ceil(maxY - minY),
    strokes,
    dims,
    notes,
  };
}

function SheetDim({ x1, y1, x2, y2, text, side, inside, outsideArrows, caption }) {
  const horizontal = Math.abs(x2 - x1) >= Math.abs(y2 - y1);
  const span = Math.hypot(x2 - x1, y2 - y1) || 1;
  let fs = 16;
  const short = outsideArrows || span < 28;
  if (inside && horizontal && !short) {
    fs = Math.max(9, Math.min(16, Math.floor((span - 8) / (String(text).length * 0.62))));
  }
  const tw = Math.max(12, String(text).length * fs * 0.62);
  const pointer = 10;
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const ux = (x2 - x1) / span;
  const uy = (y2 - y1) / span;
  const shaft = 16;
  const shelf = tw + 8;
  const textX = short
    ? x2 + ux * (shaft + shelf / 2)
    : (horizontal ? mx : mx + (side === 'right' ? 1 : -1) * (fs * 0.85));
  const textY = short
    ? y2 + uy * (shaft + shelf / 2) - (horizontal ? fs * 0.7 : 0)
    : (horizontal ? my - fs * 0.7 : my);
  const sideSign = side === 'right' ? 1 : -1;
  const placedX = short && !horizontal ? textX + sideSign * fs * 0.85 : textX;
  const arrowProps = {
    stroke: '#000000',
    fill: '#000000',
    strokeWidth: 2,
    pointerLength: pointer,
    pointerWidth: pointer,
    listening: false,
  };
  if (short) {
    const left = [x1 - ux * shaft, y1 - uy * shaft];
    const right = [x2 + ux * shaft, y2 + uy * shaft];
    const shelfEnd = [x2 + ux * (shaft + shelf), y2 + uy * (shaft + shelf)];
    return (
      <>
        <Line
          points={[left[0], left[1], shelfEnd[0], shelfEnd[1]]}
          stroke="#000000"
          strokeWidth={2}
          listening={false}
        />
        <Arrow points={[left[0], left[1], x1, y1]} {...arrowProps} pointerAtEnding />
        <Arrow points={[right[0], right[1], x2, y2]} {...arrowProps} pointerAtEnding />
        <Text
          x={placedX}
          y={textY}
          text={String(text)}
          fontSize={fs}
          fontFamily="Arial"
          fontStyle="italic"
          fill="#ff0000"
          rotation={horizontal ? 0 : -90}
          offsetX={tw / 2}
          offsetY={fs / 2}
          listening={false}
        />
      </>
    );
  }
  return (
    <>
      <Arrow
        points={[x1, y1, x2, y2]}
        {...arrowProps}
        pointerAtBeginning
        pointerAtEnding
      />
      <Text
        x={textX}
        y={textY}
        text={String(text)}
        fontSize={fs}
        fontFamily="Arial"
        fontStyle="italic"
        fill="#ff0000"
        rotation={horizontal ? 0 : -90}
        offsetX={tw / 2}
        offsetY={fs / 2}
        listening={false}
      />
      {caption && !horizontal && (
        <Text
          x={mx + fs * 0.85}
          y={textY}
          text={String(caption)}
          fontSize={fs}
          fontFamily="Arial"
          fontStyle="italic"
          fill="#ff0000"
          rotation={-90}
          offsetX={String(caption).length * fs * 0.28}
          offsetY={fs / 2}
          listening={false}
        />
      )}
    </>
  );
}

export function FastenerSheetGraphics({ sheet }) {
  if (!sheet) return null;
  return (
    <>
      {sheet.strokes.map((stroke, index) => (
        <Line
          key={`s${index}`}
          points={stroke.points}
          stroke="#111"
          strokeWidth={stroke.sw}
          dash={stroke.dash}
          lineJoin="round"
          lineCap="round"
          listening={false}
        />
      ))}
      {sheet.dims.map((dim, index) => (
        <SheetDim key={`d${index}`} {...dim} />
      ))}
      {sheet.notes.map((note, index) => (
        <React.Fragment key={`n${index}`}>
          {note.diameter && !note.arrowsOut && (
            <Arrow
              points={note.diameter}
              stroke="#000000"
              fill="#000000"
              strokeWidth={1}
              pointerLength={9}
              pointerWidth={8}
              pointerAtBeginning
              pointerAtEnding
              listening={false}
            />
          )}
          {note.diameter && note.arrowsOut && (
            <Line
              points={note.diameter}
              stroke="#000000"
              strokeWidth={1}
              listening={false}
            />
          )}
          {note.arrowsOut && note.arrowsOut.map((points, arrowIndex) => (
            <Arrow
              key={`a${arrowIndex}`}
              points={points}
              stroke="#000000"
              fill="#000000"
              strokeWidth={1}
              pointerLength={8}
              pointerWidth={7}
              listening={false}
            />
          ))}
          {Number.isFinite(note.kneeX) && note.diameter && (
            <Line
              points={[
                note.leaderFrom ? note.leaderFrom[0] : note.diameter[0],
                note.leaderFrom ? note.leaderFrom[1] : note.diameter[1],
                note.kneeX,
                note.shelfY,
              ]}
              stroke="#000000"
              strokeWidth={1}
              listening={false}
            />
          )}
          {Number.isFinite(note.kneeX) && (
            <Line
              points={[note.shelfX, note.shelfY, note.kneeX, note.shelfY]}
              stroke="#000000"
              strokeWidth={1}
              listening={false}
            />
          )}
          {Number.isFinite(note.tipX) && (
            <Arrow
              points={[
                Number.isFinite(note.kneeX) ? note.kneeX : note.x,
                Number.isFinite(note.kneeX) ? note.shelfY : note.y + 12,
                note.tipX,
                note.tipY,
              ]}
              stroke="#000000"
              fill="#000000"
              strokeWidth={1}
              pointerLength={9}
              pointerWidth={8}
              listening={false}
            />
          )}
          <Text
            x={note.anchor === 'end' ? note.x : note.x}
            y={note.y}
            width={note.width || 78}
            align={note.anchor === 'end' ? 'right' : 'left'}
            text={note.text}
            fontSize={16}
            fontFamily="Arial"
            fontStyle="italic"
            fill="#ff0000"
            listening={false}
          />
        </React.Fragment>
      ))}
    </>
  );
}

const FastenerDrawingDialog = ({ open, onClose, fastenerKey, clampDiameter, axialDistance, overhang, mixed }) => {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !fastenerKey) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setPayload(null);
    projectFastenerDrawing({
      partKey: fastenerKey,
      clampDiameter,
      axialDistance,
      overhang,
    }).then((data) => {
      if (!cancelled) setPayload(data);
    }).catch((err) => {
      if (!cancelled) setError(err?.message || 'Не удалось построить чертёж');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [open, fastenerKey, clampDiameter, axialDistance, overhang]);

  const sheet = useMemo(() => (payload ? layoutFastenerSheet(payload) : null), [payload]);
  const metrics = payload?.metrics;

  const details = [];
  if (metrics) {
    details.push(`Ø${fmt(metrics.diameter)}`);
    if (metrics.axial != null) details.push(`межосевое ${fmt(metrics.axial)}`);
    details.push(`вылет ${fmt(metrics.overhang)}`);
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6, pb: 0.5 }}>
        Чертёж {fastenerKey || ''}
        <IconButton aria-label="Закрыть" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {details.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {details.join(' · ')}
          </Typography>
        )}
        {mixed && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            На листе несколько таких крепежей с разными размерами. Показан первый.
          </Typography>
        )}
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4 }}>
            <CircularProgress size={22} />
            <Typography variant="body2">Строю виды по модели…</Typography>
          </Box>
        )}
        {error && (
          <Typography variant="body2" color="error" sx={{ py: 2 }}>{error}</Typography>
        )}
        {sheet && (
          <Box sx={{ overflow: 'auto', bgcolor: '#f3f3f3', border: '1px solid #ddd', p: 1.5 }}>
            <Stage width={sheet.width} height={sheet.height}>
              <Layer>
                <Rect width={sheet.width} height={sheet.height} fill="#fff" listening={false} />
                <FastenerSheetGraphics sheet={sheet} />
              </Layer>
            </Stage>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FastenerDrawingDialog;
